import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
      this.#setActiveMedia(index);
      dialog.showModal();
      this.#loadHighResolutionImage(targetImage);
      this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeMedia = media[this.#activeIndex];
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[this.#activeIndex]
      : mediaGallery?.media?.[this.#activeIndex];

    if (!slide || !activeMedia) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    activeMedia.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      activeMedia.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    this.selectThumbnail(index);
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async selectThumbnail(index) {
    const { media, thumbnails } = this.refs;
    if (isNaN(index) || index < 0 || index >= media.length) return;
    const targetImage = media[index];

    if (targetImage) {
      this.#setActiveMedia(index);
      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  previous() {
    const { media } = this.refs;
    if (!media.length) return;

    const index = (this.#activeIndex - 1 + media.length) % media.length;
    this.selectThumbnail(index);
  }

  next() {
    const { media } = this.refs;
    if (!media.length) return;

    const index = (this.#activeIndex + 1) % media.length;
    this.selectThumbnail(index);
  }

  #setActiveMedia(index) {
    const { media, thumbnails } = this.refs;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    this.#activeIndex = index;

    media.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.hidden = !isActive;
      item.toggleAttribute('data-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));

      if (!isActive) {
        const zoomWrapper = item.querySelector('drag-zoom-wrapper');
        if (zoomWrapper && 'reset' in zoomWrapper && typeof zoomWrapper.reset === 'function') {
          zoomWrapper.reset();
        }
      }
    });

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, itemIndex) => {
      button.setAttribute('aria-selected', String(itemIndex === index));
    });

    const counterCurrent = this.querySelector('.dialog-zoomed-gallery__counter-current');
    if (counterCurrent) counterCurrent.textContent = String(index + 1);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
